import {isFeatureEnabled} from '@github-ui/feature-flags'
import {type AssigneesSectionFragment, EditIssueAssigneesSection} from '@github-ui/issue-metadata/AssigneesSection'
import {EditIssueFieldsSection, type EditIssueFieldsSectionFragment} from '@github-ui/issue-metadata/FieldsSection'
import {EditIssueLabelsSection} from '@github-ui/issue-metadata/LabelsSection'
import {EditIssueMilestonesSection} from '@github-ui/issue-metadata/MilestonesSection'
import {EditIssueProjectsSection, ProjectsSectionFallback} from '@github-ui/issue-metadata/ProjectsSection'
import {EditIssueIssueTypeSection, type TypesSectionFragment} from '@github-ui/issue-metadata/TypesSection'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {clsx} from 'clsx'
import {Suspense, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueSidebarPrimaryQuery$key} from './__generated__/IssueSidebarPrimaryQuery.graphql'
import {FIXED_HEADER_HEIGHT} from './header/constants/sticky-elements'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import styles from './IssueSidebar.module.css'
import {
  IssueSidebarLazySections,
  IssueSidebarLazySectionsFallback,
  type IssueSidebarLazySectionsFragment,
} from './IssueSidebarLazySections'
import type {IssueViewerViewerFragment} from './IssueViewer'
import type {OptionConfig} from './OptionConfig'
import {OptionsSection, type OptionsSectionFragment} from './sections/OptionsSection'

type IssueSidebarSecondaryRepository = Pick<NonNullable<IssueSecondaryNode['repository']>, 'isLocked' | 'viewerCanPush'>
type IssueSidebarSecondarySuggestedActors = Pick<IssueSecondaryNode, 'suggestedActors'>
type IssueSidebarSecondaryFragment = RelayCompat<
  Pick<IssueSecondaryNode, 'isTransferInProgress'> &
    IssueSidebarSecondarySuggestedActors & {
      repository: IssueSidebarSecondaryRepository
    }
>

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): the secondary issue data is still read via `useFragment`
// in `useSecondaryQuery`. Remove the RelayCompat widening when IssueViewerSecondaryViewQuery leaves Relay.
export type IssueSidebarSecondaryKey = IssueSidebarSecondaryFragment

type IssueSidebarProps = {
  sidebarKey: IssueSidebarPrimaryQuery$key
  sidebarSecondaryKey?: IssueSidebarSecondaryKey
  lazySecondaryData?: IssueSidebarLazySectionsFragment
  optionConfig: OptionConfig
  viewer: IssueViewerViewerFragment | null
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueSidebarPrimaryGraphqlQuery = graphql`
  fragment IssueSidebarPrimaryQuery on Issue @argumentDefinitions(allowedOwner: {type: "String", defaultValue: null}) {
    id
    number
    repository {
      id
      databaseId
      name
      nameWithOwner
      visibility
      isOwnerEnterpriseManaged
      viewerCanPinIssues
      owner {
        login
        databaseId
        ... on Organization {
          issueFieldsEnabled: isFeatureEnabled(name: "issue_fields")
          issueFields(first: 1) {
            totalCount
          }
          pinnedIssueFields(first: 100) {
            nodes {
              __typename
              ... on IssueFieldText {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldSingleSelect {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldMultiSelect {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldDate {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldNumber {
                id
                name
                dataType
                visibility
              }
            }
          }
        }
      }
      isArchived
      isCopilotAgentEnabled
      issueTypes(first: 100) {
        edges {
          node {
            id
          }
        }
      }
      pinnedIssues(first: 3) {
        totalCount
      }
      viewerIssueCreationPermissions {
        triageable
      }
    }
    assignedActors(first: 20) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
    agentAssignments {
      assignee {
        id
        login
        avatarUrl(size: 64)
        isCopilot
        isAgent
        displayName
      }
      assigner {
        __typename
        ... on Node {
          id
        }
        ... on Actor {
          login
        }
      }
    }
    viewerCanAssign
    labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
        }
      }
    }
    viewerCanLabel
    ...ProjectsSectionFragment @arguments(allowedOwner: $allowedOwner)
    id
    milestone {
      id
      title
      closed
      dueOn
      progressPercentage
      url
      closedAt
    }
    viewerCanSetMilestone
    number
    title
    body
    isPinned
    locked
    viewerCanDelete
    viewerCanTransfer
    viewerCanConvertToDiscussion
    viewerCanLock
    issueType {
      id
      name
      isEnabled
      description
      color
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldMultiSelect {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldDate {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
          visibility
        }
      }
    }
    viewerCanType
    viewerCanSetFields
    issueFieldValues(first: 25) {
      nodes {
        ... on IssueFieldTextValue {
          field {
            ... on IssueFieldText {
              id
              name
              dataType
              visibility
            }
          }
          value
        }
        ... on IssueFieldSingleSelectValue {
          field {
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
              visibility
            }
          }
          optionId
          name
          color
          description
        }
        ... on IssueFieldMultiSelectValue {
          field {
            ... on IssueFieldMultiSelect {
              id
              name
              dataType
              visibility
            }
          }
          options {
            id
            name
            color
            description
          }
          multiSelectValue: value
        }
        ... on IssueFieldDateValue {
          field {
            ... on IssueFieldDate {
              id
              name
              dataType
              visibility
            }
          }
          value
        }
        ... on IssueFieldNumberValue {
          field {
            ... on IssueFieldNumber {
              id
              name
              dataType
              visibility
            }
          }
          numberValue: value
        }
      }
    }
  }
`

export function IssueSidebar({
  sidebarKey,
  sidebarSecondaryKey: issueSecondary,
  lazySecondaryData,
  optionConfig,
  viewer,
}: IssueSidebarProps) {
  const issue = useFragment(IssueSidebarPrimaryGraphqlQuery, sidebarKey)
  const modernUx = isFeatureEnabled('issues_modern_ux')

  const sidebar = useMemo(() => {
    const viewerCanPush = issueSecondary?.repository?.viewerCanPush ?? false
    const isRepoLocked = issueSecondary?.repository?.isLocked ?? false
    const readonly = optionConfig.isReadonly || isRepoLocked

    return (
      <div
        className={clsx(styles.sidebarContent, modernUx && styles.sidebarContentModernUx)}
        data-modern-ux={modernUx || undefined}
      >
        <EditIssueAssigneesSection
          // RELAY-CLEANUP(IssueSidebarPrimaryQuery): IssueSidebarPrimaryQuery is still Relay-owned.
          // Remove this cast when IssueSidebarPrimaryQuery leaves Relay.
          issue={issue as AssigneesSectionFragment}
          viewer={viewer}
          // RELAY-CLEANUP(IssueViewerSecondaryViewQuery): the still-Relay secondary query shape for
          // `suggestedActors` is wider than the migrated plain-props type. Remove when
          // IssueViewerSecondaryViewQuery leaves Relay.
          lazyKey={issueSecondary ?? undefined}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueLabelsSection
          issue={issue}
          canCreateLabel={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueIssueTypeSection
          // RELAY-CLEANUP(IssueSidebarPrimaryQuery): open-enum and nullable field widening from the still-Relay parent.
          // Remove this cast when IssueSidebarPrimaryQuery leaves Relay.
          issue={issue as TypesSectionFragment}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <EditIssueFieldsSection
          // RELAY-CLEANUP(IssueSidebarPrimaryQuery): open-enum and nullable field widening from the still-Relay parent.
          // Remove this cast when IssueSidebarPrimaryQuery leaves Relay.
          issue={issue as EditIssueFieldsSectionFragment}
          onUpdate={optionConfig.onIssueUpdate}
          inProjectsPage={!!optionConfig.selectedProjectId}
          readonly={readonly}
        />
        <Suspense fallback={<ProjectsSectionFallback />}>
          <ErrorBoundary fallback={<ProjectsSectionFallback />}>
            <EditIssueProjectsSection
              selectedProjectId={optionConfig.selectedProjectId}
              allowedProjectOwner={optionConfig.allowedProjectOwner}
              issueOrPullRequest={issue}
              onIssueUpdate={optionConfig.onIssueUpdate}
              onRemovedFromProject={optionConfig.onRemovedFromProject}
              insideSidePanel={optionConfig.insideSidePanel}
              readonly={readonly}
            />
          </ErrorBoundary>
        </Suspense>
        <EditIssueMilestonesSection
          issue={issue}
          canCreateMilestone={viewerCanPush}
          onIssueUpdate={optionConfig.onIssueUpdate}
          insideSidePanel={optionConfig.insideSidePanel}
          readonly={readonly}
        />
        <Suspense fallback={<IssueSidebarLazySectionsFallback />}>
          <IssueSidebarLazySections
            onLinkClick={optionConfig.onLinkClick}
            onIssueUpdate={optionConfig.onIssueUpdate}
            insideSidePanel={optionConfig.insideSidePanel}
            issueData={lazySecondaryData}
            viewer={viewer}
            readonly={readonly}
          />
        </Suspense>
        {!readonly && (
          <OptionsSection
            // RELAY-CLEANUP(IssueSidebarPrimaryQuery): open-enum `visibility` comes from the still-Relay parent.
            // Remove when IssueSidebarPrimaryQuery leaves Relay.
            issue={issue as OptionsSectionFragment}
            optionsSectionSecondary={issueSecondary}
            optionConfig={optionConfig}
          />
        )}
      </div>
    )
  }, [issue, issueSecondary, lazySecondaryData, optionConfig, viewer, modernUx])

  return <StickySidebar topOffset={FIXED_HEADER_HEIGHT}>{sidebar}</StickySidebar>
}
